import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, type PublicBrand } from "@/api/client";

const Ctx = createContext<PublicBrand | null>(null);

function hexToRgbTriplet(hex: string | undefined): string | null {
  if (!hex || !/^#?[0-9a-f]{6}$/i.test(hex)) return null;
  const h = hex.replace("#", "");
  return `${parseInt(h.slice(0, 2), 16)} ${parseInt(h.slice(2, 4), 16)} ${parseInt(h.slice(4, 6), 16)}`;
}

/** Puts a brand on the document: `data-brand` picks the token set in styles.css; an explicit theme colour on the
 * brand overrides the accent. Used by the public site (brand by host) and by the admin shell (brand of the store). */
export function applyBrandTheme(slug: string, theme?: Record<string, string>) {
  const root = document.documentElement;
  root.setAttribute("data-brand", slug);
  const accent = hexToRgbTriplet(theme?.primary);
  if (accent) root.style.setProperty("--c-accent", accent); else root.style.removeProperty("--c-accent");
  const strong = hexToRgbTriplet(theme?.accent);
  if (strong) root.style.setProperty("--c-accent-strong", strong); else root.style.removeProperty("--c-accent-strong");
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<PublicBrand | null>(null);
  useEffect(() => {
    const override = new URLSearchParams(location.search).get("brand");
    api<PublicBrand>(`/public/brand${override ? `?brand=${override}` : ""}`)
      .then((b) => {
        setBrand(b);
        applyBrandTheme(b.slug, b.theme);
        document.title = b.name.el ?? b.name.en ?? "STUDIO";
      })
      .catch(() => setBrand(null));
  }, []);
  return <Ctx.Provider value={brand}>{children}</Ctx.Provider>;
}

export const useBrand = () => useContext(Ctx);
